export type SignalDisplayMode = 'off' | 'steady' | 'blink' | 'fast_blink' | 'flash';

export interface SignalStatus {
  id: string;
  name: string;
  color: string;
  mode: SignalDisplayMode;
}

export interface SignalSlot {
  id: string;
  name: string;
  previewStatus?: string;
  statuses: SignalStatus[];
}

export interface SignalsMetadata {
  version: string;
  name: string;
  description: string;
}

export interface SignalsDefinition {
  metadata: SignalsMetadata;
  /**
   * MQTT base topic, e.g. `signals1`.
   * Per signal: `{base}/{SignalName}` with atomic status name payload (`rot`).
   * Effects: `{base}/effect` with atomic effect name (`chase`).
   * Effect colour: `{base}/color` with `#rrggbb` or a named colour (`rot`).
   */
  mqttTopic?: string;
  signals: SignalSlot[];
}

export const MAX_SIGNAL_SLOTS = 8;

export const SIGNAL_DISPLAY_MODES: SignalDisplayMode[] = [
  'off',
  'steady',
  'blink',
  'fast_blink',
  'flash',
];

export const SIGNALS_MQTT_EFFECTS = [
  'chase',
  'rotate',
  'faces',
  'knightrider',
  'segment',
  'fill',
  'cycle',
  'off',
] as const;

export type SignalsMqttEffect = (typeof SIGNALS_MQTT_EFFECTS)[number];

/** Topic path segment from a signal display name (`Signal 1` → `Signal1`). */
export function signalsTopicSegment(name: string): string {
  const cleaned = String(name || '').trim().replace(/\s+/g, '');
  return cleaned || 'signal';
}

export function signalsBaseTopic(defOrBase: SignalsDefinition | string | null | undefined): string {
  if (typeof defOrBase === 'string') {
    return defOrBase.trim().replace(/\/+$/, '');
  }
  return String(defOrBase?.mqttTopic || '').trim().replace(/\/+$/, '');
}

/** `{base}/{SignalName}` — atomic status name payload. */
export function signalsSignalTopic(base: string, signalName: string): string {
  const b = signalsBaseTopic(base);
  if (!b) return '';
  return `${b}/${signalsTopicSegment(signalName)}`;
}

/** `{base}/effect` — atomic effect name payload. */
export function signalsEffectTopic(base: string): string {
  const b = signalsBaseTopic(base);
  return b ? `${b}/effect` : '';
}

/** `{base}/color` — effect base colour (`#rrggbb` or a named colour). */
export function signalsColorTopic(base: string): string {
  const b = signalsBaseTopic(base);
  return b ? `${b}/color` : '';
}

/** Normalize MQTT / picker colour text to `#rrggbb`, or null if unknown. */
export function signalsColorToHex(raw: string | null | undefined): string | null {
  const t = String(raw || '').trim();
  if (!t) return null;
  const h = t.charAt(0) === '#' ? t.slice(1) : t;
  if (/^[0-9a-fA-F]{6}$/.test(h)) return `#${h.toLowerCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(h)) {
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return null;
}

/** All MQTT topics to subscribe for a signals definition. */
export function signalsMqttTopics(def: SignalsDefinition | null | undefined): string[] {
  const base = signalsBaseTopic(def);
  if (!base) return [];
  const topics = (def?.signals || []).map((s) => signalsSignalTopic(base, s.name)).filter(Boolean);
  const effect = signalsEffectTopic(base);
  if (effect) topics.push(effect);
  const color = signalsColorTopic(base);
  if (color) topics.push(color);
  return Array.from(new Set(topics));
}
